/* cwclientlibjs
   copyright 2015 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import {BoundRqlQuery} from './types';

/**
 * Generate a RQL query to retrieve the current state of a workflowable entity
 *
 * @param eid Identifier of the workflowable entity
 */
export function currentState(eid: number): BoundRqlQuery<{eid: number}> {
    const rql = `
        Any
            STATE_NAME
        WHERE
            ENTITY eid %(eid)s,
            ENTITY in_state STATE,
            STATE name STATE_NAME
    `;
    return [rql, {eid}];
}

/**
 * Generates a RQL query to retrieve the list of transitions that can be fired
 * on a given entity
 *
 * The query doesn't take the user rights into account. It means that some
 * transitions could not be fireable by the user executing the query but still
 * appear in the results.
 * Each resulting row contains a single value which represents a transition
 * name.
 * @param eid Identifier of the workflowable entity
 */
export function possibleTransitions(eid: number): BoundRqlQuery<{eid: number}> {
    const rql = ` Any TRANSITION_NAME WHERE ENTITY eid %(eid)s,
            ENTITY in_state CURRENT_STATE,
            CURRENT_STATE allowed_transition TRANSITION,
            TRANSITION name TRANSITION_NAME
    `;
    return [rql, {eid}];
}

/**
 * Generates a RQL query to fire a transition on an entity
 *
 * The result contains a single row containing only the eid of the created TrInfo.
 * @param eid Identifier of the workflowable entity on which to fire the transition
 * @param transitionName Name of transition to fire
 * @param options.comment Additional comment to store in the created TrInfo
 */
export function fireTransition(
    eid: number,
    transitionName: string,
    options: {comment?: string} = {}
): BoundRqlQuery<{eid: number; transitionName: string; comment?: string}> {
    const vars: {eid: number; transitionName: string; comment?: string} = {
        eid,
        transitionName,
    };
    let commentAssignment = '';
    if (options.comment) {
        vars.comment = options.comment;
        commentAssignment = ', TR_INFO comment %(comment)s';
    }
    const rql = `
        INSERT TrInfo TR_INFO:
            TR_INFO wf_info_for ENTITY, TR_INFO by_transition TRANSITION${commentAssignment}
        WHERE
            ENTITY eid %(eid)s,
            ENTITY in_state CURRENT_STATE, CURRENT_STATE state_of WORKFLOW,
            TRANSITION transition_of WORKFLOW, TRANSITION name %(transitionName)s
    `;
    return [rql, vars];
}
